import { useCallback } from 'react';

/**
 * useMultiCampaignHandlers Hook
 * 
 * Handles multi-campaign mode specific logic for SprintDetail.
 * Includes view rename/icon handlers and campaign picker handling.
 */
const useMultiCampaignHandlers = ({
  isMultiCampaignMode,
  onRenameView,
  onViewIconChange,
  handlers,
  tasks,
  setIsCampaignPickerOpen,
  campaignPickerMode,
  setCampaignPickerMode,
  pendingPasteDataRef,
  flowchartRef,
}) => {
  // View rename handler (for multi-campaign mode)
  const handleViewRename = useCallback((newName) => {
    if (isMultiCampaignMode && onRenameView) {
      onRenameView(newName);
    }
  }, [isMultiCampaignMode, onRenameView]);

  // View icon change handler (for multi-campaign mode)
  const handleViewIconChangeLocal = useCallback((newIcon) => {
    if (isMultiCampaignMode && onViewIconChange) {
      onViewIconChange(newIcon);
    }
  }, [isMultiCampaignMode, onViewIconChange]);

  // Handle campaign selection from picker (multi-campaign mode)
  const handleCampaignPickerSelect = useCallback((campaign) => {
    setIsCampaignPickerOpen(false);
    
    if (campaignPickerMode === 'paste' && pendingPasteDataRef?.current) {
      // Paste mode: paste clipboard data into selected campaign
      const pasteData = pendingPasteDataRef.current;
      pendingPasteDataRef.current = null;
      
      // Find position in the campaign using the same logic as add mission
      let targetPosition = { x: 100, y: 40 };
      if (flowchartRef?.current?.findPositionInCampaign) {
        const result = flowchartRef.current.findPositionInCampaign(campaign.id, tasks);
        targetPosition = result.position;
        
        // Apply campaign adjustments if any (shift other campaigns to the right)
        if (result.campaignAdjustments) {
          Object.values(result.campaignAdjustments).forEach((adjustment) => {
            if (adjustment.positionShifts) {
              Object.entries(adjustment.positionShifts).forEach(([taskId, newPos]) => {
                flowchartRef.current?.setTaskPosition(taskId, newPos);
              });
            }
          });
        }
      }
      
      // Calculate the campaign offset for proper save-back
      const existingCampaignTask = tasks.find(t => 
        t.sourceCampaign?.id === campaign.id && t._campaignOffset !== undefined
      );
      const campaignOffset = existingCampaignTask?._campaignOffset || 0;
      
      // Call the paste handler with campaign info
      handlers.handlePasteWithCampaign?.({
        pasteData,
        targetPosition,
        sourceCampaign: campaign,
        campaignOffset,
        allTasks: tasks,
      });
    } else {
      // Add mode: add new task to the selected campaign
      handlers.handleAddTaskAtViewport?.({ 
        sourceCampaign: campaign,
        allTasks: tasks,
      });
    }
    
    // Reset mode to default
    setCampaignPickerMode('add');
  }, [handlers, tasks, setIsCampaignPickerOpen, campaignPickerMode, setCampaignPickerMode, pendingPasteDataRef, flowchartRef]);

  // Open campaign picker for paste operation
  const openCampaignPickerForPaste = useCallback((clipboardData) => {
    if (!isMultiCampaignMode) return false;
    
    pendingPasteDataRef.current = clipboardData;
    setCampaignPickerMode('paste');
    setIsCampaignPickerOpen(true);
    return true;
  }, [isMultiCampaignMode, setIsCampaignPickerOpen, setCampaignPickerMode, pendingPasteDataRef]);

  return {
    handleViewRename,
    handleViewIconChangeLocal,
    handleCampaignPickerSelect,
    openCampaignPickerForPaste,
  };
};

export default useMultiCampaignHandlers;
